import { useRef, type ChangeEvent } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useNavigate } from 'react-router';
import { Alert } from '../Alert';
import { Button, type ButtonVariant } from '../Button';
import { api } from '../../lib/api';
import { isApiCode, messageForError } from '../../lib/errors';
import type { ReceiptNavState } from '../../pages/groceries/receipt';

/**
 * "Scan receipt": opens the camera (or the photo picker), sends the photo to
 * be read and opens the review screen with the proposal. The server reads the
 * photo in memory and never stores it.
 */
export function ScanReceiptButton({
  listId,
  variant = 'secondary',
  label = 'Scan receipt',
  replace = false,
}: {
  listId: string;
  variant?: ButtonVariant;
  label?: string;
  /** Replace the current history entry (re-scanning from the review screen). */
  replace?: boolean;
}) {
  const input = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const scan = useMutation({
    mutationFn: (file: File) => api.groceries.receipt.scan(listId, file),
    onSuccess: (proposal) => {
      const state: ReceiptNavState = { proposal };
      void navigate(`/groceries/${listId}/receipt`, { replace, state });
    },
  });

  const onPick = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    // Reset so picking the same photo again still fires `change`.
    e.target.value = '';
    if (file) scan.mutate(file);
  };

  return (
    <div className="flex flex-col gap-2">
      <input
        ref={input}
        type="file"
        accept="image/*"
        capture="environment"
        className="sr-only"
        tabIndex={-1}
        aria-hidden
        onChange={onPick}
      />
      <Button
        variant={variant}
        block={false}
        disabled={scan.isPending}
        onClick={() => input.current?.click()}
      >
        {scan.isPending ? 'Reading receipt…' : label}
      </Button>
      {scan.isError ? (
        <Alert tone={isApiCode(scan.error, 'RECEIPT_SCAN_UNAVAILABLE') ? 'info' : 'error'}>
          {messageForError(scan.error)}
        </Alert>
      ) : null}
    </div>
  );
}
